import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import DraggableModal from 'components/organisms/draggableModal';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import FormItemWrapper from 'components/molecules/rpm-alert-assessment/formItemWrapper';

import { criticalNoCallForm } from '../templates/criticalNoCall';
import s from '../styles.module.scss';
import { useCriticalNoCall } from '../hooks/use-critical-no-call';
import { useStoreModalValue } from '../hooks/use-store-modal-value';
import { criticalNoCallValidationSchema } from '../validation-schema';
import { scrollToErrorElement } from '../helpers';

import type { ICriticalNoCallProps, TFormItem } from '../types';
import type { FC } from 'react';

const CriticalNoCallModal: FC<ICriticalNoCallProps> = ({
  isOpen,
  type,
  readingType,
  idx,
  handleSubmit,
  closeModal,
}) => {
  const {
    doctor: { addComment },
  } = useDispatch();

  const { t } = useTranslation();

  const { initialValues, onSaveForm } = useCriticalNoCall({
    addComment,
    readingType,
    closeModal,
    handleSubmit,
  });

  type TInitialValues = typeof initialValues;
  const { parsedData, setModalValuesToStorage } =
    useStoreModalValue<TInitialValues>({
      idx,
      type,
      modalName: 'criticalNoCall',
    });
  const blockRef = useRef<HTMLDivElement | null>(null);
  const formWrapperRef = useRef<HTMLDivElement>(null);

  const formik = useFormik({
    initialValues: parsedData ?? initialValues,
    onSubmit: (data) => {
      if (!data.escalatedVia.includes('Other')) {
        data.escalatedViaOther = null;
      }
      setModalValuesToStorage(data);

      onSaveForm(data);
    },
    validateOnChange: false,
    validationSchema: criticalNoCallValidationSchema(),
  });

  const onSave = async () => {
    await formik.submitForm();
    scrollToErrorElement(formWrapperRef.current);
  };
  const isCallIndicated = 'isCallIndicated';
  const isReadingEscalated = 'isReadingEscalated';
  const whyNotIndicated = 'whyNotIndicated';
  const notEscalatedReasons = 'notEscalatedReasons';
  const escalatedVia = 'escalatedVia';

  const showExtraFields = (formItem: TFormItem) => {
    const fieldValue = formik.getFieldMeta(formItem.name)?.value;

    let isShowExtraFields = false;
    if (formItem.name === isCallIndicated) {
      isShowExtraFields = fieldValue === 'false';
    } else if (formItem.name === isReadingEscalated) {
      isShowExtraFields = fieldValue === 'true' || fieldValue === 'false';
    }

    let isShowExtraExtraFields = false;
    if (formItem.name === isCallIndicated && fieldValue === 'false') {
      const whyNotValue = formik.getFieldMeta(whyNotIndicated)?.value;
      isShowExtraExtraFields = whyNotValue?.includes('Other');
    }
    if (formItem.name === isReadingEscalated && fieldValue === 'false') {
      const notEscalatedValue = formik.getFieldMeta(notEscalatedReasons)?.value;
      isShowExtraExtraFields = notEscalatedValue?.includes('Other');
    }
    if (formItem.name === isReadingEscalated && fieldValue === 'true') {
      const escalatedViaValue = formik.values[escalatedVia];
      isShowExtraExtraFields = escalatedViaValue?.includes('Other');
    }

    return {
      isShowExtraFields,
      isShowExtraExtraFields,
    };
  };

  return (
    <>
      <DraggableModal
        isOpen={isOpen}
        toggle={closeModal}
        title={t('labels.criticalNoCall')}
        onSave={onSave}
      >
        <BaseForm formik={formik}>
          <div className={s.wrapper} ref={formWrapperRef}>
            {criticalNoCallForm.map((formItem, index) => {
              const { isShowExtraFields, isShowExtraExtraFields } =
                showExtraFields(formItem);

              return (
                <FormItemWrapper
                  formik={formik}
                  index={index}
                  formItem={formItem}
                  type={type}
                  requiredSymptomFields={[]}
                  ref={blockRef}
                  isShowExtraFields={isShowExtraFields}
                  isShowExtraExtraFields={isShowExtraExtraFields}
                />
              );
            })}
          </div>
        </BaseForm>
      </DraggableModal>
    </>
  );
};

export default CriticalNoCallModal;
